import * as React from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '../../utils/cn';
import { QUESTIONNAIRE_COLOUR } from './questionnaire-tokens';

export interface OptionCardOption {
    value: string;
    title: string;
    description?: string;
}

export interface OptionCardGroupProps {
    options: OptionCardOption[];
    value?: string;
    onChange: (value: string) => void;
    /** Accessible name for the group. */
    label: string;
    /**
     * Draws a rule above this option, setting whatever follows apart from what
     * came before — a way out held away from a list of real choices, say.
     *
     * Presentational only: the options stay one group, because they are still
     * one choice however they are spaced.
     */
    dividerBefore?: string;
    /** Small label sitting on that rule. */
    dividerLabel?: string;
    className?: string;
}

/**
 * The stacked single-select answer: a title, a line of description and a
 * chevron, six to a step.
 *
 * Radio semantics rather than buttons, so the whole group is one tab stop and
 * arrow keys move between options — which is what a reader who navigates by
 * keyboard expects of a single choice, and what a row of buttons would not give
 * them. Roving tabindex keeps the selected option (or the first, when nothing
 * is chosen yet) as the only member in the tab order.
 */
export const OptionCardGroup = ({
    options,
    value,
    onChange,
    label,
    dividerBefore,
    dividerLabel,
    className,
}: OptionCardGroupProps) => {
    const refs = React.useRef<(HTMLButtonElement | null)[]>([]);

    const focusOption = (index: number) => {
        const next = (index + options.length) % options.length;
        const option = options[next];
        if (!option) return;
        refs.current[next]?.focus();
        onChange(option.value);
    };

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLButtonElement>,
        index: number,
    ) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault();
            focusOption(index + 1);
        }
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault();
            focusOption(index - 1);
        }
    };

    const activeIndex = Math.max(
        options.findIndex((option) => option.value === value),
        0,
    );

    return (
        <div
            aria-label={label}
            className={cn('flex flex-col gap-2', className)}
            role="radiogroup"
        >
            {options.map((option, index) => {
                const selected = option.value === value;
                const divider = dividerBefore === option.value && index > 0;

                return (
                    <React.Fragment key={option.value}>
                        {divider ? (
                            <div
                                aria-hidden="true"
                                className="flex items-center gap-3 pt-3 pb-1"
                            >
                                <span
                                    className={cn(
                                        'h-px flex-1',
                                        'bg-[#eaeaea]',
                                    )}
                                />
                                {dividerLabel ? (
                                    <span
                                        className={cn(
                                            'font-sans text-2.75 tracking-[0.03em] uppercase',
                                            QUESTIONNAIRE_COLOUR.inkLabel,
                                        )}
                                    >
                                        {dividerLabel}
                                    </span>
                                ) : null}
                                <span
                                    className={cn(
                                        'h-px flex-1',
                                        'bg-[#eaeaea]',
                                    )}
                                />
                            </div>
                        ) : null}
                        <button
                            aria-checked={selected}
                            className={cn(
                                'flex w-full items-center gap-2.5 rounded-3 p-4 text-left transition-colors',
                                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#181818]',
                                selected
                                    ? cn(
                                          QUESTIONNAIRE_COLOUR.surfaceSelected,
                                          'text-white',
                                      )
                                    : cn(
                                          QUESTIONNAIRE_COLOUR.surface,
                                          QUESTIONNAIRE_COLOUR.surfaceHover,
                                      ),
                            )}
                            onClick={() => onChange(option.value)}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                            ref={(node) => {
                                refs.current[index] = node;
                            }}
                            role="radio"
                            tabIndex={index === activeIndex ? 0 : -1}
                            type="button"
                        >
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                <span
                                    className={cn(
                                        'font-sans text-4 leading-[1.3] font-medium tracking-[-0.01em]',
                                        selected
                                            ? 'text-white'
                                            : QUESTIONNAIRE_COLOUR.ink,
                                    )}
                                >
                                    {option.title}
                                </span>
                                {option.description ? (
                                    <span
                                        className={cn(
                                            'font-sans text-4 leading-[1.3] tracking-[-0.01em]',
                                            selected
                                                ? 'text-white/70'
                                                : QUESTIONNAIRE_COLOUR.inkSubtle,
                                        )}
                                    >
                                        {option.description}
                                    </span>
                                ) : null}
                            </span>
                            <ChevronRight
                                aria-hidden="true"
                                className="size-4.5 shrink-0"
                            />
                        </button>
                    </React.Fragment>
                );
            })}
        </div>
    );
};
